import {
  containsMarkup,
  FORMULATOR_AREAS_NOTE_MAX,
  FORMULATOR_DRAFT_ITEM_MAX,
  FORMULATOR_DRAFT_ITEMS_MAX,
  FORMULATOR_DRAFT_TEXT_MAX,
  type FormulatorDraft,
} from "@ttc-spectree/toolbox-contract";
import { sanitizeAdoHtml } from "@/lib/ado-html";
import type { InterviewReview } from "@/lib/api/work-item-interview";
import { splitReproSteps } from "./repro-steps";

/**
 * The review screen's draft editor values (epic PI-SPEC-SW-E321, feature
 * PI-SPEC-SW-1721): the contract's draft as plain text boxes, one entry per
 * line for the lists, and back.
 *
 * The draft is plain text (the contract refuses markup); SpecTree renders it
 * into the item's HTML. Every function here is pure; exported for tests.
 */

export interface StoryEditValues {
  kind: "story";
  description: string;
  /** One criterion per line. */
  acceptanceCriteria: string;
  areasNote: string;
}

export interface BugEditValues {
  kind: "bug";
  /** One step per line. */
  reproSteps: string;
  expected: string;
  actual: string;
  frequency: string;
  systemInfo: string;
  areasNote: string;
}

export type DraftEditValues = StoryEditValues | BugEditValues;

/** One text box of the editor, by kind. */
export type DraftEditField = Exclude<keyof StoryEditValues | keyof BugEditValues, "kind">;

/** How long one text box may be, and whether it is one entry per line. */
export interface DraftFieldLimit {
  /** Most characters in the box (single text), or in one line (list). */
  max: number;
  /** True for the one-per-line lists (acceptance criteria, repro steps). */
  list: boolean;
  /** True when the box may be left empty. */
  optional: boolean;
}

/** The contract's caps, per text box. */
export const DRAFT_FIELD_LIMITS: Record<DraftEditField, DraftFieldLimit> = {
  description: { max: FORMULATOR_DRAFT_TEXT_MAX, list: false, optional: false },
  acceptanceCriteria: { max: FORMULATOR_DRAFT_ITEM_MAX, list: true, optional: false },
  reproSteps: { max: FORMULATOR_DRAFT_ITEM_MAX, list: true, optional: false },
  expected: { max: FORMULATOR_DRAFT_TEXT_MAX, list: false, optional: false },
  actual: { max: FORMULATOR_DRAFT_TEXT_MAX, list: false, optional: false },
  frequency: { max: FORMULATOR_DRAFT_ITEM_MAX, list: false, optional: true },
  systemInfo: { max: FORMULATOR_DRAFT_TEXT_MAX, list: false, optional: true },
  areasNote: { max: FORMULATOR_AREAS_NOTE_MAX, list: false, optional: true },
};

/** The text boxes in reading order, with the labels a Product Owner sees. */
export const DRAFT_EDIT_FIELDS: Record<DraftEditValues["kind"], { field: DraftEditField; label: string }[]> = {
  story: [
    { field: "description", label: "Story" },
    { field: "acceptanceCriteria", label: "Done when (one per line)" },
    { field: "areasNote", label: "Areas affected (optional)" },
  ],
  bug: [
    { field: "reproSteps", label: "Repro steps (one per line)" },
    { field: "expected", label: "Expected" },
    { field: "actual", label: "Actual" },
    { field: "frequency", label: "Frequency (optional)" },
    { field: "systemInfo", label: "System info (optional)" },
    { field: "areasNote", label: "Areas affected (optional)" },
  ],
};

/** The value of one text box; "" for a box the kind does not have. */
export function editValue(values: DraftEditValues, field: DraftEditField): string {
  return (values as unknown as Record<string, string | undefined>)[field] ?? "";
}

/** `values` with one text box changed. */
export function withEditValue(values: DraftEditValues, field: DraftEditField, text: string): DraftEditValues {
  return { ...values, [field]: text } as DraftEditValues;
}

/** The non-blank lines of a one-per-line box, trimmed. */
export function editLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

/** The editor values of a stored draft. */
export function editValuesFromDraft(draft: FormulatorDraft): DraftEditValues {
  if (draft.kind === "bug") {
    return {
      kind: "bug",
      reproSteps: (draft.reproSteps ?? []).join("\n"),
      expected: draft.expected ?? "",
      actual: draft.actual ?? "",
      frequency: draft.frequency ?? "",
      systemInfo: draft.systemInfo ?? "",
      areasNote: draft.areasNote ?? "",
    };
  }
  return {
    kind: "story",
    description: draft.description ?? "",
    acceptanceCriteria: (draft.acceptanceCriteria ?? []).join("\n"),
    areasNote: draft.areasNote ?? "",
  };
}

function parse(html: string): HTMLElement | null {
  const clean = sanitizeAdoHtml(html);
  if (clean === null) return null;
  return new DOMParser().parseFromString(clean, "text/html").body;
}

/**
 * The text of a node with each `<br>` read as `lineBreak` (`textContent`
 * alone drops it, so `Windows 11<br>Chrome 130` would read
 * `Windows 11Chrome 130`). Paragraph text keeps it as a line break; a list
 * entry, which is one line, reads it as a space.
 */
function textOf(node: Node, lineBreak: "\n" | " " = "\n"): string {
  const copy = node.cloneNode(true);
  if (copy instanceof Element) {
    for (const br of [...copy.querySelectorAll("br")]) br.replaceWith(lineBreak);
  }
  return (copy.textContent ?? "")
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .trim();
}

function entryText(node: Node): string {
  return textOf(node, " ");
}

/**
 * Plain text of sanitised HTML: each top-level block a paragraph (separated
 * by a blank line), each list item on a line of its own with its marker
 * (`1. ` numbered, `- ` bulleted), the form SpecTree renders back into a list.
 * The one-entry-per-line list boxes use {@link htmlToEditLines} instead,
 * which has no markers.
 */
export function htmlToEditText(html: string): string {
  const body = parse(html);
  if (!body) return "";
  const blocks: string[] = [];
  for (const node of [...body.childNodes]) {
    if (node instanceof HTMLOListElement || node instanceof HTMLUListElement) {
      // Keep the list markers: SpecTree turns "1. " and "- " lines back into a
      // numbered or bulleted list, so saving an unrelated edit keeps the list.
      const numbered = node instanceof HTMLOListElement;
      const items = [...node.querySelectorAll("li")].map(entryText).filter((line) => line !== "");
      if (items.length > 0) blocks.push(items.map((item, index) => (numbered ? `${String(index + 1)}. ${item}` : `- ${item}`)).join("\n"));
      continue;
    }
    const text = textOf(node);
    if (text !== "") blocks.push(text);
  }
  return blocks.join("\n\n");
}

/** The entries of an HTML list (or, with no list, its paragraphs), one per line. */
export function htmlToEditLines(html: string): string {
  const body = parse(html);
  if (!body) return "";
  const items = [...body.querySelectorAll("li")].map(entryText).filter((line) => line !== "");
  if (items.length > 0) return items.join("\n");
  return editLines(htmlToEditText(html)).join("\n");
}

/**
 * The editor values of a new item whose text was put together from the
 * answers (no draft yet): read back from the review's rendered fields, so the
 * person starts from exactly what they see. Only for create mode, where the
 * fields hold nothing but the new text.
 */
export function editValuesFromReview(review: Pick<InterviewReview, "kind" | "fields">): DraftEditValues {
  const after = (field: string) => review.fields.find((diff) => diff.field === field)?.after ?? "";
  if (review.kind === "bug") {
    const split = splitReproSteps(after("reproSteps"));
    const labelled = (label: string) => split.labelled.find((row) => row.label.toLowerCase() === label)?.text ?? "";
    return {
      kind: "bug",
      reproSteps: split.stepsHtml === "" ? "" : htmlToEditLines(split.stepsHtml),
      expected: labelled("expected"),
      actual: labelled("actual"),
      frequency: labelled("frequency"),
      systemInfo: htmlToEditText(after("systemInfo")),
      areasNote: "",
    };
  }
  return {
    kind: "story",
    description: htmlToEditText(after("description")),
    acceptanceCriteria: htmlToEditLines(after("acceptanceCriteria")),
    areasNote: "",
  };
}

/** Whether the interview writes a new item or adds to an existing one. */
export type DraftEditMode = "create" | "improve";

/**
 * True when the box may be left empty. In create mode only the optional
 * boxes may; in improve mode every box may, because an improve draft carries
 * only what the interview added and an empty box keeps the item's text as it
 * is ({@link editValuesValid} still needs at least one box with text).
 */
function mayBeEmpty(field: DraftEditField, mode: DraftEditMode): boolean {
  return mode === "improve" || DRAFT_FIELD_LIMITS[field].optional;
}

/** What is wrong with one text box, in plain words; null when it is fine. */
export function editProblem(values: DraftEditValues, field: DraftEditField, mode: DraftEditMode = "create"): string | null {
  const limit = DRAFT_FIELD_LIMITS[field];
  const text = editValue(values, field);
  if (limit.list) {
    const lines = editLines(text);
    if (lines.length === 0) return mayBeEmpty(field, mode) ? null : "Add at least one line.";
    if (lines.length > FORMULATOR_DRAFT_ITEMS_MAX) return `Use at most ${String(FORMULATOR_DRAFT_ITEMS_MAX)} lines.`;
    if (lines.some((line) => line.length > limit.max)) return `Keep each line to ${String(limit.max)} characters or fewer.`;
    if (lines.some(containsMarkup)) return "Use plain text only; remove anything that looks like an HTML tag.";
    return null;
  }
  const trimmed = text.trim();
  if (trimmed === "") return mayBeEmpty(field, mode) ? null : "This cannot be empty.";
  if (trimmed.length > limit.max) return `Keep this to ${String(limit.max)} characters or fewer.`;
  if (containsMarkup(trimmed)) return "Use plain text only; remove anything that looks like an HTML tag.";
  return null;
}

/** True when no text box has any text: an improve draft needs at least one. */
export function editValuesEmpty(values: DraftEditValues): boolean {
  return DRAFT_EDIT_FIELDS[values.kind].every(({ field }) => editValue(values, field).trim() === "");
}

/**
 * True when every text box of `values` can be saved. In improve mode at
 * least one box must have text.
 */
export function editValuesValid(values: DraftEditValues, mode: DraftEditMode = "create"): boolean {
  if (mode === "improve" && editValuesEmpty(values)) return false;
  return DRAFT_EDIT_FIELDS[values.kind].every(({ field }) => editProblem(values, field, mode) === null);
}

function optional(text: string): string | undefined {
  const trimmed = text.trim();
  return trimmed === "" ? undefined : trimmed;
}

function optionalLines(text: string): string[] | undefined {
  const lines = editLines(text);
  return lines.length === 0 ? undefined : lines;
}

/**
 * The contract draft for the editor values, carrying `basis` (the basis the
 * review showed). Empty boxes are left out (in create mode only optional
 * boxes can be empty, see {@link editValuesValid}); lists drop blank lines.
 */
export function draftFromEditValues(values: DraftEditValues, basis: string): FormulatorDraft {
  if (values.kind === "bug") {
    const reproSteps = optionalLines(values.reproSteps);
    const expected = optional(values.expected);
    const actual = optional(values.actual);
    const frequency = optional(values.frequency);
    const systemInfo = optional(values.systemInfo);
    const areasNote = optional(values.areasNote);
    return {
      kind: "bug",
      ...(reproSteps !== undefined ? { reproSteps } : {}),
      ...(expected !== undefined ? { expected } : {}),
      ...(actual !== undefined ? { actual } : {}),
      ...(frequency !== undefined ? { frequency } : {}),
      ...(systemInfo !== undefined ? { systemInfo } : {}),
      ...(areasNote !== undefined ? { areasNote } : {}),
      basis,
    };
  }
  const description = optional(values.description);
  const acceptanceCriteria = optionalLines(values.acceptanceCriteria);
  const areasNote = optional(values.areasNote);
  return {
    kind: "story",
    ...(description !== undefined ? { description } : {}),
    ...(acceptanceCriteria !== undefined ? { acceptanceCriteria } : {}),
    ...(areasNote !== undefined ? { areasNote } : {}),
    basis,
  };
}
